import React, { useCallback } from 'react'
import { useNewTranslation } from 'hooks'
import InputField from 'components/Shared/Fields'
import { BlockFormEditorProps } from '../BlockEditor'
import { UploadType } from 'services/api/uploads'
import { useSelector } from 'react-redux'
import { selectRegistrationLengthLimits } from 'state/constants'
import Divider from 'components/Shared/Divider'
import { useField, useFormikContext } from 'formik'
import RectangleButton from 'particles/Button/RectangleButton'
import Toggle from 'particles/Toggle'
import { fieldIdentifiers } from 'components/Shared/Comment'
import RangeInput from 'particles/RangeInput'

interface TextBlockEditorProps extends BlockFormEditorProps {
    title?: string
    isReadOnly?: boolean
}

interface BlockRequiredToggleProps extends BlockFormEditorProps {
    identifier?: string
}

const TextBlockEditor: React.FC<TextBlockEditorProps> = ({
    namePrefix,
    isDisabled,
    title,
    isReadOnly = false,
    blockUuid,
}) => {
    const [t] = useNewTranslation()
    const registrationLengthLimit = useSelector(selectRegistrationLengthLimits)

    return (
        <>
            <InputField.TextInputField
                name={`${namePrefix}.title`}
                width="100%"
                label={title ? title : t('campaignEditor.createTask.enterTitleHeader')}
                isRequired
                showInputLength
                maxInputLength={registrationLengthLimit.blocks.title}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                identifier={`${blockUuid}_${fieldIdentifiers.blockTitle}`}
            />
            <InputField.TextAreaField
                name={`${namePrefix}.description`}
                label={t('campaignEditor.createTask.enterDescriptionHeader')}
                width="100%"
                showInputLength
                maxInputLength={registrationLengthLimit.blocks.description}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                identifier={`${blockUuid}_${fieldIdentifiers.blockDescription}`}
            />
        </>
    )
}

const PhotosBlockEditor: React.FC<BlockFormEditorProps> = ({
    blockUuid,
    namePrefix,
    isDisabled,
    isReadOnly = false,
}) => {
    const [t] = useNewTranslation()
    const registrationLengthLimit = useSelector(selectRegistrationLengthLimits)

    return (
        <>
            <InputField.ImageUploadField
                presetType={UploadType.CAMPAIGN}
                maxFiles={10}
                title={t('campaignEditor.customAndRegistrationField.uploadPhotoHeader')}
                subtitle={t('campaignEditor.createTask.uploadFilesExampleSubtext')}
                name={`${namePrefix}.images`}
                uploadContainerWidth="594px"
                isDisable={isDisabled}
                isReadOnly={isReadOnly}
                isRequired
                identifier={`${blockUuid}_${fieldIdentifiers.blockImages}`}
            />
            <InputField.TextAreaField
                name={`${namePrefix}.description`}
                label={t('campaignEditor.createTask.enterDescriptionHeader')}
                width="100%"
                showInputLength
                maxInputLength={registrationLengthLimit.blocks.description}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                identifier={`${blockUuid}_${fieldIdentifiers.blockDescription}`}
            />
        </>
    )
}

interface BaseBlockRequiredToggleProps {
    name: string
    title: string
    description: string
    isDisabled?: boolean
    isReadOnly?: boolean
    identifier?: string
}

const BaseBlockRequiredToggle: React.FC<BaseBlockRequiredToggleProps> = ({
    name,
    title,
    description,
    isDisabled,
    isReadOnly = false,
    identifier,
}) => {
    const [meta] = useField(name)
    const { setFieldValue } = useFormikContext()
    const { value: isRequired } = meta

    return (
        <Toggle
            isChecked={isRequired}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            handleChange={() => {
                const updateValue = !Boolean(isRequired)
                setFieldValue(name, updateValue)
            }}
            label={title}
            subtext={description}
            identifier={identifier}
        />
    )
}

const BlockRequiredToggle: React.FC<BlockRequiredToggleProps> = ({
    namePrefix,
    isDisabled,
    isReadOnly = false,
    blockUuid,
}) => {
    const [t] = useNewTranslation()

    return (
        <BaseBlockRequiredToggle
            name={`${namePrefix}.is_required`}
            title={t('campaignEditor.customAndRegistrationField.turnOnRequiredQuestionHeader')}
            description={t('campaignEditor.customAndRegistrationField.turnOnRequiredQuestionSubtext')}
            isDisabled={isDisabled}
            isReadOnly={isReadOnly}
            identifier={`${blockUuid}_${fieldIdentifiers.blockIsRequired}`}
        />
    )
}

const PhotoSubmissionBlockEditor: React.FC<BlockFormEditorProps> = ({
    blockUuid,
    namePrefix,
    isDisabled,
    isReadOnly = false,
    canEditCertainFields = false,
}) => {
    const [t] = useNewTranslation()
    return (
        <>
            <BlockRequiredToggle
                namePrefix={namePrefix}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                blockUuid={blockUuid}
            />
            <Divider />
            <div>
                <TextBlockEditor
                    namePrefix={namePrefix}
                    isDisabled={isDisabled}
                    isReadOnly={canEditCertainFields ? false : isReadOnly}
                    blockUuid={blockUuid}
                />
                <InputField.NumberInputField
                    name={`${namePrefix}.data.number_of_photos`}
                    title={t('campaignEditor.customAndRegistrationField.enterMaximumPhotosHeader')}
                    subtitle={t('campaignEditor.customAndRegistrationField.enterMaximumPhotosSubtext')}
                    placeholder="0"
                    isDisable={isDisabled}
                    isRequired
                    isReadOnly={isReadOnly}
                    identifier={`${blockUuid}_${fieldIdentifiers.numberOfPhotos}`}
                />
            </div>
        </>
    )
}

const TextSubmissionBlockEditor: React.FC<BlockFormEditorProps> = ({
    blockUuid,
    namePrefix,
    isDisabled,
    isReadOnly = false,
    canEditCertainFields = false,
}) => {
    const [t] = useNewTranslation()
    return (
        <>
            <BlockRequiredToggle
                namePrefix={namePrefix}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                blockUuid={blockUuid}
            />
            <Divider />
            <BaseBlockRequiredToggle
                name={`${namePrefix}.data.is_long_answer`}
                title={t('campaignEditor.customAndRegistrationField.turnOnParagraphFormHeader')}
                description={t('campaignEditor.customAndRegistrationField.turnOnParagraphFormSubtext')}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                identifier={`${blockUuid}_${fieldIdentifiers.blockIsLongAnswer}`}
            />
            <Divider />
            <TextBlockEditor
                blockUuid={blockUuid}
                namePrefix={namePrefix}
                isDisabled={isDisabled}
                isReadOnly={canEditCertainFields ? false : isReadOnly}
            />
        </>
    )
}

const SubmissionBlockEditor: React.FC<BlockFormEditorProps> = ({
    namePrefix,
    isDisabled,
    isReadOnly = false,
    canEditCertainFields = false,
    blockUuid,
}) => {
    return (
        <>
            <BlockRequiredToggle
                namePrefix={namePrefix}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                blockUuid={blockUuid}
            />
            <Divider />
            <TextBlockEditor
                namePrefix={namePrefix}
                isDisabled={isDisabled}
                isReadOnly={canEditCertainFields ? false : isReadOnly}
                blockUuid={blockUuid}
            />
        </>
    )
}

const QuestionList: React.FC<Omit<BlockFormEditorProps, 'blockUuid'>> = ({
    namePrefix,
    isDisabled,
    isReadOnly = false,
    canEditCertainFields = false,
}) => {
    const [t] = useNewTranslation()
    const [field] = useField<any>(namePrefix)
    const { setFieldValue, setFieldTouched } = useFormikContext()
    const fieldValue = field.value || []
    const registrationLengthLimit = useSelector(selectRegistrationLengthLimits)

    const addItem = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>): void => {
            e.preventDefault()
            const items = fieldValue
            items.push({ option: '' })
            setFieldValue(field.name as never, items)
        },
        [fieldValue],
    )

    const removeItem = useCallback(
        (index: number) =>
            (e: React.MouseEvent<HTMLButtonElement>): void => {
                e.preventDefault()
                const items = [...fieldValue]
                items.splice(index, 1)
                setFieldValue(field.name as never, items)
                if (items.length === 0) setFieldTouched(field.name as never, true, false)
            },
        [fieldValue],
    )

    return (
        <>
            {fieldValue.map((value: { uuid: string }, index: number) => {
                const fieldName = `${field.name}[${index}].option`
                return (
                    <InputField.TextInputField
                        key={index}
                        name={fieldName}
                        width="100%"
                        label={t('campaignEditor.customAndRegistrationField.enterOptionHeader', {
                            optionNumber: index + 1,
                        })}
                        isRequired
                        showInputLength
                        maxInputLength={registrationLengthLimit.blocks.title}
                        showDelete={!isReadOnly && !isDisabled && fieldValue.length > 2}
                        onDelete={removeItem(index)}
                        isDisabled={isDisabled}
                        isReadOnly={canEditCertainFields ? false : isReadOnly}
                        identifier={`${value.uuid}_${fieldIdentifiers.options}`}
                    />
                )
            })}
            {!isReadOnly ? (
                <RectangleButton
                    form="outlined"
                    size="s"
                    icon="plus"
                    label={t('button.addOption')}
                    isDisabled={isDisabled}
                    onClick={addItem}
                    className="width-fit-content"
                />
            ) : null}
        </>
    )
}

const MultiAnswer: React.FC<BlockFormEditorProps> = ({ blockUuid, namePrefix, isDisabled, isReadOnly = false }) => {
    const [meta] = useField(`${namePrefix}.data.isMultiAnswer`)
    const { value: isMultiAnswer } = meta
    const [t] = useNewTranslation()

    return (
        <>
            <BaseBlockRequiredToggle
                name={`${namePrefix}.data.isMultiAnswer`}
                title={t('campaignEditor.customAndRegistrationField.turnOnMultipleSelectionHeader')}
                description={t('campaignEditor.customAndRegistrationField.turnOnMultipleSelectionSubtext')}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                identifier={`${blockUuid}_${fieldIdentifiers.blockIsMultiAnswer}`}
            />
            {isMultiAnswer ? (
                <RangeInput.Number
                    label={t('campaignEditor.customAndRegistrationField.numberOfAnswersAllowedHeader')}
                    firstRangeTitle={t('campaignEditor.customAndRegistrationField.enterMinimumNumberHeader')}
                    firstRangeName={`${namePrefix}.data.question_number.min`}
                    firstRangePlaceholder="0"
                    firstRangeIdentifier={`${blockUuid}_${fieldIdentifiers.blockQuestionNumMin}`}
                    secondRangeTitle={t('campaignEditor.customAndRegistrationField.enterMaximumNumberHeader')}
                    secondRangeName={`${namePrefix}.data.question_number.max`}
                    secondRangePlaceholder="0"
                    secondRangeIdentifier={`${blockUuid}_${fieldIdentifiers.blockQuestionNumMax}`}
                    width="100%"
                    inputWidth="100%"
                    isDisabled={isDisabled}
                    isReadOnly={isReadOnly}
                    isFormikComponent
                />
            ) : null}
        </>
    )
}

const QuestionBlockEditor: React.FC<BlockFormEditorProps> = ({
    blockUuid,
    namePrefix,
    isDisabled,
    isReadOnly = false,
    canEditCertainFields = false,
}) => {
    const [t] = useNewTranslation()
    return (
        <>
            <BlockRequiredToggle
                namePrefix={namePrefix}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                blockUuid={blockUuid}
            />
            <Divider />
            <MultiAnswer
                namePrefix={namePrefix}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                blockUuid={blockUuid}
            />
            <Divider />
            <TextBlockEditor
                namePrefix={namePrefix}
                title={t('campaignEditor.customAndRegistrationField.enterQuestionHeader')}
                isDisabled={isDisabled}
                isReadOnly={canEditCertainFields ? false : isReadOnly}
                blockUuid={blockUuid}
            />
            <QuestionList
                namePrefix={`${namePrefix}.options`}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                canEditCertainFields={canEditCertainFields}
            />
            <Divider />
            <BaseBlockRequiredToggle
                name={`${namePrefix}.hasOthers`}
                title={t('campaignEditor.customAndRegistrationField.turnOnOtherAnswerHeader')}
                description={t('campaignEditor.customAndRegistrationField.turnOnOtherAnswerSubtext')}
                isDisabled={isDisabled}
                isReadOnly={isReadOnly}
                identifier={`${blockUuid}_${fieldIdentifiers.blockHasOthers}`}
            />
        </>
    )
}

export {
    TextBlockEditor,
    PhotosBlockEditor,
    PhotoSubmissionBlockEditor,
    SubmissionBlockEditor,
    TextSubmissionBlockEditor,
    QuestionBlockEditor,
}
